import { ChevronRight } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { cn } from './cn';

export interface DisclosureProps {
  /** Trigger label. */
  title: ReactNode;
  children: ReactNode;
  /** Short summary shown at the end of the trigger row (e.g. "15 pairs"). */
  meta?: ReactNode;
  /** Optional icon before the title. */
  icon?: ReactNode;
  defaultOpen?: boolean;
  /** Controlled open state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** `card` renders a bordered panel; `plain` is a bare row for use inside cards. */
  variant?: 'card' | 'plain';
  /** Wrap the trigger in a heading of this level for document outline. */
  headingLevel?: 2 | 3 | 4;
  className?: string;
  panelClassName?: string;
  /** Exposed for tests and anchors; also used to derive the panel id. */
  id?: string;
}

/**
 * Accessible show/hide section: a button with `aria-expanded` controlling a
 * region that animates its height. Collapsed content stays mounted (it keeps
 * its state) but is hidden from assistive technology and the tab order.
 */
export const Disclosure = ({
  title,
  children,
  meta,
  icon,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  variant = 'card',
  headingLevel,
  className,
  panelClassName,
  id,
}: DisclosureProps) => {
  const autoId = useId();
  const baseId = id ?? `disclosure-${autoId}`;
  const triggerId = `${baseId}-trigger`;
  const panelId = `${baseId}-panel`;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = openProp ?? uncontrolledOpen;
  const state = open ? 'open' : 'closed';

  const toggle = () => {
    const next = !open;
    if (openProp === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const trigger = (
    <button
      id={triggerId}
      type="button"
      aria-expanded={open}
      aria-controls={panelId}
      data-state={state}
      onClick={toggle}
      className={cn(
        'group flex w-full select-none items-center gap-2 text-left text-sm font-medium text-fg transition-colors duration-150',
        variant === 'card'
          ? 'rounded-xl px-4 py-3 hover:bg-surface-muted/60 data-[state=open]:rounded-b-none sm:px-5'
          : '-mx-2 rounded-md px-2 py-1.5 hover:bg-surface-muted'
      )}
    >
      <ChevronRight
        aria-hidden="true"
        className="size-4 shrink-0 text-fg-muted transition-transform duration-200 ease-out group-data-[state=open]:rotate-90"
      />
      {icon != null && (
        <span aria-hidden="true" className="flex text-fg-muted [&_svg]:size-4">
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{title}</span>
      {meta != null && (
        <span className="shrink-0 text-xs font-normal text-fg-muted">
          {meta}
        </span>
      )}
    </button>
  );

  const Heading = headingLevel ? (`h${headingLevel}` as const) : null;

  return (
    <div
      data-state={state}
      className={cn(
        variant === 'card' &&
          'rounded-xl border border-border bg-surface shadow-sm',
        className
      )}
    >
      {Heading ? <Heading className="m-0">{trigger}</Heading> : trigger}
      <div
        id={panelId}
        aria-hidden={!open}
        data-state={state}
        className={cn(
          'grid transition-[grid-template-rows,visibility] duration-200 ease-out',
          open ? 'visible grid-rows-[1fr]' : 'invisible grid-rows-[0fr]'
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div
            className={cn(
              variant === 'card'
                ? 'border-t border-border px-4 py-4 sm:px-5'
                : 'pt-2',
              panelClassName
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};
